'use client'

import { useState } from 'react'
import Image, { type ImageProps } from 'next/image'
import { LuImage } from 'react-icons/lu'
import { cn } from '@/core/utils/common'

type SafeImageProps = ImageProps & {
  containerClassName?: string
}

export default function SafeImage(props: SafeImageProps) {
  return <SafeImageContent key={getSourceKey(props.src)} {...props} />
}

function getSourceKey(src: ImageProps['src']): string {
  if (typeof src === 'string') return src
  return 'default' in src ? src.default.src : src.src
}

function SafeImageContent({
  className,
  containerClassName,
  alt,
  onError,
  fill,
  src,
  ...props
}: SafeImageProps) {
  const [hasError, setHasError] = useState(false)

  return (
    <div
      className={cn(
        'overflow-hidden',
        fill ? 'absolute inset-0' : 'relative inline-block',
        containerClassName
      )}
    >
      {/* 에러 폴백 */}
      {hasError && (
        <div
          role={alt ? 'img' : undefined}
          aria-label={alt || undefined}
          aria-hidden={alt ? undefined : true}
          className="absolute inset-0 flex items-center justify-center bg-foreground/5 text-description"
        >
          <LuImage aria-hidden="true" className="w-8 h-8" />
        </div>
      )}

      {/* SSR 이미지 표시는 hydration에 의존하지 않는다. 실패해도 치수를 유지한다. */}
      <Image
        {...props}
        src={src}
        alt={hasError ? '' : alt}
        aria-hidden={hasError || undefined}
        fill={fill}
        className={cn(className, hasError && 'invisible')}
        onError={(e) => {
          setHasError(true)
          onError?.(e)
        }}
      />
    </div>
  )
}
